import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Plus, 
  Receipt, 
  Trash2, 
  Edit, 
  FileText, 
  Upload,
  TrendingDown,
  Calendar,
  Building2,
  Search
} from "lucide-react";

const formatCurrency = (amount, devise = "FCFA") => {
  if (typeof amount !== "number" || isNaN(amount)) return "0 FCFA";
  if (devise === "EUR") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  }
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

const MODES_PAIEMENT = [
  { value: "virement", label: "Virement bancaire" },
  { value: "cheque", label: "Chèque" },
  { value: "especes", label: "Espèces" },
  { value: "mobile_money", label: "Mobile Money" },
  { value: "carte", label: "Carte bancaire" },
];

const emptyDepense = {
  categorie: "",
  description: "",
  montant: "",
  date_depense: new Date().toISOString().split("T")[0],
  mode_paiement: "virement",
  fournisseur: "",
  reference_facture: "",
  notes: "",
};

export default function Depenses() {
  const [depenses, setDepenses] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDepense, setEditingDepense] = useState(null);
  const [formData, setFormData] = useState(emptyDepense);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [depensesRes, categoriesRes] = await Promise.all([
        apiClient.get("/depenses"),
        apiClient.get("/depenses/categories")
      ]);
      setDepenses(depensesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des dépenses");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        montant: parseFloat(formData.montant),
      };

      if (editingDepense) {
        await apiClient.put(`/depenses/${editingDepense.id}`, payload);
        toast.success("Dépense mise à jour");
      } else {
        await apiClient.post("/depenses", payload);
        toast.success("Dépense enregistrée avec écriture comptable");
      }
      setDialogOpen(false);
      setEditingDepense(null);
      setFormData(emptyDepense);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (depense) => {
    setEditingDepense(depense);
    setFormData({
      categorie: depense.categorie,
      description: depense.description,
      montant: depense.montant.toString(),
      date_depense: depense.date_depense,
      mode_paiement: depense.mode_paiement,
      fournisseur: depense.fournisseur || "",
      reference_facture: depense.reference_facture || "",
      notes: depense.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try {
      await apiClient.delete(`/depenses/${id}`);
      toast.success("Dépense supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const filteredDepenses = depenses.filter((d) => {
    const matchSearch = 
      d.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.fournisseur?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.reference_facture?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategorie = filterCategorie === "all" || d.categorie === filterCategorie;
    return matchSearch && matchCategorie;
  });

  const totalDepenses = filteredDepenses.reduce((acc, d) => acc + (d.montant || 0), 0);

  const depensesParCategorie = depenses.reduce((acc, d) => {
    const cat = d.categorie_label || d.categorie || "Autres";
    acc[cat] = (acc[cat] || 0) + (d.montant || 0);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="depenses-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Dépenses</h1>
          <p className="text-zinc-500 mt-1">Suivi et gestion des charges de l'entreprise</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => { setEditingDepense(null); setFormData(emptyDepense); setDialogOpen(true); }}
          data-testid="add-depense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle dépense
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-600/20 rounded-lg">
                <TrendingDown className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Total dépenses</p>
                <p className="text-xl font-bold text-white">{formatCurrency(totalDepenses)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600/20 rounded-lg">
                <Receipt className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Nombre de dépenses</p>
                <p className="text-xl font-bold text-white">{depenses.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-600/20 rounded-lg">
                <FileText className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-500 text-sm">Catégories actives</p>
                <p className="text-xl font-bold text-white">{Object.keys(depensesParCategorie).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Répartition par catégorie */}
      {Object.keys(depensesParCategorie).length > 0 && (
        <Card className="bg-[#121214] border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white">Répartition par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(depensesParCategorie)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, montant]) => (
                  <Badge 
                    key={cat} 
                    variant="outline" 
                    className="border-zinc-700 text-zinc-300 py-1 px-3"
                  >
                    {cat}: {formatCurrency(montant)}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher une dépense..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-800"
          />
        </div>
        <Select value={filterCategorie} onValueChange={setFilterCategorie}>
          <SelectTrigger className="w-[200px] bg-zinc-900/50 border-zinc-800">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-zinc-800">
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {Object.entries(categories).map(([key, cat]) => (
              <SelectItem key={key} value={key}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-[#121214] border-zinc-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Date</TableHead>
                <TableHead className="text-zinc-400">Catégorie</TableHead>
                <TableHead className="text-zinc-400">Description</TableHead>
                <TableHead className="text-zinc-400">Fournisseur</TableHead>
                <TableHead className="text-zinc-400">Référence</TableHead>
                <TableHead className="text-zinc-400 text-right">Montant</TableHead>
                <TableHead className="text-zinc-400 text-center">Compte OHADA</TableHead>
                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDepenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-zinc-500 py-8">
                    Aucune dépense enregistrée
                  </TableCell>
                </TableRow>
              ) : (
                filteredDepenses.map((depense) => (
                  <TableRow key={depense.id} className="border-zinc-800">
                    <TableCell className="text-zinc-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-zinc-500" />
                        {new Date(depense.date_depense).toLocaleDateString("fr-FR")}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-zinc-700 text-zinc-300">
                        {depense.categorie_label || depense.categorie}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-300 max-w-[200px] truncate">
                      {depense.description}
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {depense.fournisseur ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-zinc-500" />
                          {depense.fournisseur}
                        </div>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-zinc-400 font-mono text-sm">
                      {depense.reference_facture || "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-red-400">
                      -{formatCurrency(depense.montant)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-zinc-800 text-zinc-300 font-mono">
                        {depense.compte_ohada}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(depense)}
                          className="text-zinc-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(depense.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">
              {editingDepense ? "Modifier la dépense" : "Nouvelle dépense"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Catégorie *</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(v) => setFormData({ ...formData, categorie: v })}
                  required
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="depense-categorie-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800 max-h-[300px]">
                    {Object.entries(categories).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Date *</Label>
                <Input
                  type="date"
                  value={formData.date_depense}
                  onChange={(e) => setFormData({ ...formData, date_depense: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="bg-zinc-900/50 border-zinc-800"
                placeholder="Description de la dépense"
                data-testid="depense-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Montant (FCFA) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="50000"
                  data-testid="depense-montant-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Mode de paiement</Label>
                <Select
                  value={formData.mode_paiement}
                  onValueChange={(v) => setFormData({ ...formData, mode_paiement: v })}
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {MODES_PAIEMENT.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Fournisseur</Label>
                <Input
                  value={formData.fournisseur}
                  onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="Nom du fournisseur"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">N° Facture / Référence</Label>
                <Input
                  value={formData.reference_facture}
                  onChange={(e) => setFormData({ ...formData, reference_facture: e.target.value })}
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="FAC-2024-001"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-zinc-900/50 border-zinc-800 min-h-[80px]"
                placeholder="Notes supplémentaires..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700"
              >
                Annuler
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="depense-submit-btn">
                {editingDepense ? "Mettre à jour" : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
